import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Scale, Loader2, KeyRound } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../lib/api'

type Step = 'credentials' | 'mfa'

export default function Login() {
  const { login, sessionExpired } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [step, setStep] = useState<Step>('credentials')
  const [mfaCode, setMfaCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const opts =
        step === 'mfa'
          ? useRecovery
            ? { recoveryCode: mfaCode.trim() }
            : { mfaCode: mfaCode.trim() }
          : undefined
      const result = await login(email, password, opts)
      if (result.kind === 'ok') {
        navigate(from, { replace: true })
        return
      }
      if (result.kind === 'enrollment-required') {
        // Forzamos navegación a Seguridad y mostramos aviso.
        navigate('/settings/security', { replace: true })
        return
      }
      // mfa-required
      setStep('mfa')
      setMfaCode('')
      setError(null)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? 'No se pudo iniciar sesión.')
      } else {
        setError('No se pudo conectar con el servidor.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 font-heebo t-primary relative overflow-hidden">
      <div className="fixed inset-0 -z-10 bg-[#0b1226]" />
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(closest-side at 30% 20%, rgba(191,43,70,0.35), transparent 60%), radial-gradient(closest-side at 70% 80%, rgba(124,58,237,0.25), transparent 65%)',
        }}
      />

      <div className="w-full max-w-md surface p-8 animate-slideUp">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-toga-500/20 text-toga-300 mb-3 shadow-glow-toga">
            <Scale size={28} />
          </div>
          <h1 className="text-3xl font-serif font-bold gradient-text-toga">Toga</h1>
          <p className="t-muted text-sm mt-1">CRM para bufetes</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {sessionExpired && step === 'credentials' && !error && (
            <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              Tu sesión ha caducado por seguridad. Vuelve a iniciar sesión para continuar.
            </div>
          )}
          {step === 'credentials' ? (
            <>
              <div>
                <label className="block text-sm t-secondary mb-1.5" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 transition"
                  placeholder="admin@toga.local"
                />
              </div>
              <div>
                <label className="block text-sm t-secondary mb-1.5" htmlFor="password">
                  Contraseña
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 transition"
                />
              </div>
            </>
          ) : (
            <>
              <div className="text-center pb-2">
                <KeyRound size={22} className="mx-auto text-toga-300 mb-1" />
                <p className="text-sm t-primary font-semibold">Verificación en dos pasos</p>
                <p className="text-xs t-muted mt-1">
                  Introduce el código actual de tu app de autenticación.
                </p>
              </div>
              <div>
                <label className="block text-sm t-secondary mb-1.5" htmlFor="mfa-code">
                  {useRecovery ? 'Código de recuperación' : 'Código TOTP (6 dígitos)'}
                </label>
                <input
                  id="mfa-code"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  maxLength={useRecovery ? 20 : 8}
                  placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 transition font-mono text-center tracking-widest"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setUseRecovery((v) => !v)
                  setMfaCode('')
                }}
                className="text-xs t-muted hover:text-toga-300 underline-offset-2 hover:underline"
              >
                {useRecovery
                  ? '← Usar código TOTP'
                  : '¿Perdiste tu dispositivo? Usar código de recuperación'}
              </button>
            </>
          )}

          {error && (
            <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={
              submitting ||
              (step === 'credentials' ? !email || !password : !mfaCode)
            }
            className="w-full py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting
              ? step === 'mfa'
                ? 'Verificando…'
                : 'Iniciando sesión…'
              : step === 'mfa'
                ? 'Verificar'
                : 'Iniciar sesión'}
          </button>

          {step === 'mfa' && (
            <button
              type="button"
              onClick={() => {
                setStep('credentials')
                setMfaCode('')
                setError(null)
              }}
              className="w-full text-xs t-muted hover:t-secondary"
            >
              ← Volver
            </button>
          )}
        </form>

        <p className="t-faint text-xs text-center mt-6">
          ¿Has olvidado tu contraseña? Pide a un administrador que te genere una nueva.
        </p>
      </div>
    </div>
  )
}
